import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import type { DashboardDto, DashboardRowDto } from '../../api/dashboard';
import { useDashboard } from '../../api/dashboard';
import { Badge, EmptyState, ErrorState, Pagination, Select } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { useQueryParams, type ParamValue } from '../../lib/query-params';
import { useVenueDirectory } from '../quotas/dictionary';
import { DEPARTMENT_BUCKET_LABEL, departmentFilterOptions } from '../../departments';
import { useDepartmentJoin } from '../../departments-query';
import { ContractFunnel } from './ContractFunnel';

import './dashboard.css';
import {
  APPLICATIONS_PATH,
  VENUE_TABLE_PAGE_SIZE,
  PARAM_DEPARTMENT,
  PARAM_DIR,
  PARAM_KIND,
  PARAM_REGION,
  PARAM_SORT,
  clampPage,
  drillDownSearch,
  axisByStatusOf,
  isPartnerRow,
  kindBadgeLabel,
  kindFilterLabel,
  kindFilterOptions,
  kindFormat,
  offlineFillOf,
  offlinePlanOf,
  onlineFillOf,
  onlinePlanOf,
  pageSlice,
  parseDepartmentParam,
  parseDir,
  parseKindFilterParam,
  parseSort,
  regionsOf,
  sectionColumns,
  totalPagesOf,
  venueTables,
  type PartnerTablePlan,
  type SortKey,
  type VenueAxis,
  type VenueTablePlan,
} from './dashboard-model';
import { formatCount } from './format';
import { FillBar, StatusBadges } from './StatusBadges';
import { StatusExplainer } from './StatusExplainer';
import { CampaignCards, CampaignCardsSkeleton } from './StatCards';

/** Control status is a venue-admin field (T.C4a); contract #11 does not carry it. */
const CONTROL_PLACEHOLDER = '—';
const CONTROL_HINT = 'Статус контроля задаётся на карточке площадки (T.C4a)';

/**
 * Dashboard И1 (task T.C2, cards reworked by T.R3, per-type plan by T.Y9): the
 * filter strip, the campaign summary and the per-venue fill tables. The controls
 * in the strip are the region (server-side, contract #11 takes `?region=`), —
 * ADDITIVE (T.Z42) — the «Отдел» (server-side since T.Z56/v1.8.9, see
 * `dashboard-model`) and — ADDITIVE (T.Y1, v1.9.35) — the «Тип площадки»
 * (server-side from day one: the same cards-must-answer rule, over
 * `?kind=REGISTRY|PARTNER_ONLINE` — T.Z145 took the kind OFF the read again, below);
 * the «Формат обучения» pills are GONE (Р11/D1) — but T.Z144 brought the AXIS back in
 * the kind's company (below). The kind filter selects VENUES by their reporting kind
 * and lets the api recompute the campaign around them — it never sums the two types.
 *
 * REWORKED (T.Z144, owner 18.09 evening) — «Тип площадки» is a QUOTA AXIS now,
 * and the tables carry it:
 *   * «вместе» (default) → the «Офлайн-площадки» table (offline axis), and BELOW it
 *     an «Онлайн-площадки» table in the SAME formatting whose «План»/«Заполненность»
 *     read the ONLINE axis (`planOnline` / `fillPercentOnline`, v1.9.37), with its
 *     OWN pager. The T.Z68 folded «Онлайн-филиалы (пул партнёра)» section is REMOVED:
 *     the owner called it useless, and the branch rows now sit where the online work
 *     is readable — under a plan and a fill, not under a hint that said they
 *     sum to nothing;
 *   * «Офлайн» → ONE table titled «Офлайн»: the venues with an OFFLINE quota
 *     set (`planOffline > 0`), the offline plan/fill in the cells, and every
 *     register drill-in — cards, funnel sections, rows — carrying
 *     `format=OFFLINE` ({@link kindFormat}). The #11 read is narrowed by the same
 *     parameter (T.F4's filter, documented again in v1.9.37), so a card's number
 *     is provably the row count its click answers;
 *   * «Онлайн» → ONE table titled «Онлайн»: the venues with an ONLINE quota set,
 *     the online plan/fill in the cells, `format=ONLINE` on every drill-in, its
 *     own pager.
 *
 * CORRECTED (T.Z145, owner 19.09) — T.Z144 also paired the axis with T.Y1's
 * `?kind=` NARROWING on the read and a client-side kind guard in the fold, and the
 * owner caught the pair hiding the REGISTRY venues that carry an ONLINE quota from
 * the «Онлайн» view: «Looks like you filtered by type of the venue, but we have to
 * use quota based approach — show there those who have online quota». The read is
 * UN-narrowed now — `region × department × format`, never `kind` (#11 keeps
 * accepting `?kind=`, this page just stopped sending it) — and EVERY table cuts by
 * `plan > 0` on its own axis over every row that arrived, of either kind. That also
 * heals the T.Z144 drill invariant: the register takes `format=` but no kind, so a
 * kind-narrowed card never equalled the rows its own click answered; on the axis
 * read it does. The two plan cards («План офлайн», «Онлайн-пул партнёра») stop
 * zeroing under a kind — plans do not move with the axis (the api's own rule), and
 * they drill to screens that take no format.
 *
 * CORRECTED AGAIN (T.Z156, owner 21.09, v1.9.45) — the axis tables' «Заявления» cell
 * rendered the WHOLE-FUNNEL `byStatus`, so the «Онлайн-площадки» table showed offline
 * children (МФЮА: «1 | 18» beside a 0% online fill; ВолгГТУ-Камышинский «4 | 149»
 * beside 88% = 97/110). The api now answers `onlineByStatus`/`offlineByStatus` per row
 * (v1.9.45) and every quota table reads its OWN axis map ({@link axisByStatusOf}); a
 * venue with zero applications of the axis format prints the absence marker — honest
 * zeros, the row STAYS because a quota table lists quota holders (T.Z145's design).
 * The row drill-in carries the table's axis `format=`, so the register answers exactly
 * the rows the cell counted.
 *
 * The summary is a grid of clickable stat cards ({@link CampaignCards}); what a
 * card says and where it leads is `./cards`' data, not JSX. T.Z20 briefly added
 * an eleventh one — «Несвязанные», the applications with no площадка — and
 * T.Z20b took it off again: the owner wants that bucket surfaced as a condition
 * to act on, so it lives on the register as the red alert above the table.
 *
 * Two cache entries are always mounted: the unfiltered campaign feeds the
 * region picker (a scoped response only knows its own region) and the scoped
 * one drives cards + table. With no region selected the two keys coincide, so
 * TanStack serves a single request.
 *
 * ADDITIVE (T.Z42, narrowed by T.Z56) — the «Отдел» filter. T.Z42 narrowed the venue
 * ROWS in the browser and left the campaign cards global; the owner's 14.09 request
 * reversed that: «карточки должны пересчитываться по отделу, не только строки». Since
 * contract v1.8.9 #11 takes `?department=` and narrows BOTH halves server-side — the
 * campaign block AND the venue rows come back already filtered, so the cards can never
 * disagree with the table under them. The page's own join (the #6 directory × the
 * #25.5 departments, `useDepartmentJoin`) now feeds only the OPTION COUNTS, scoped to
 * the selected region, so an option keeps promising the same venues whatever is
 * selected right now.
 */
export function DashboardPage() {
  const { params, setParams } = useQueryParams();
  const navigate = useNavigate();

  const region = params[PARAM_REGION];
  const department = parseDepartmentParam(params[PARAM_DEPARTMENT]);
  const sort = parseSort(params[PARAM_SORT]);
  const dir = parseDir(params[PARAM_DIR], sort);
  // ADDITIVE (T.Y1, v1.9.35) — the «Тип площадки» filter; absent (the default) =
  // «вместе». CORRECTED (T.Z145): the kind names the AXIS the page reads — it is
  // parsed here, written by the select, and carried by the drill-ins — but it never
  // travels on the READ again (see the T.Z145 note in the file head and
  // `dashboard-model`): the tables cut the un-narrowed rows by quota.
  const kind = parseKindFilterParam(params[PARAM_KIND]);
  // ADDITIVE (T.Z144, v1.9.37) — the axis the kind names, on the wire: the register
  // drill-ins carry it AND #11 narrows its application buckets by it, so a card's
  // printed count and the rows its click answers stay the same list. This is the ONE
  // parameter of the kind filter that still reaches the api.
  const format = kindFormat(kind);

  // T.Z56 — the table + cards read the (region × department × format) response;
  // the region picker keeps reading the unfiltered one (a filtered response only knows
  // its own region). With nothing selected the two keys coincide and TanStack serves a
  // single request. A department or format change refetches the FIRST read — the api
  // recomputes both halves server-side — which is what makes the cards answer the
  // filter. T.Z145: `kind` is deliberately NOT a parameter here — a kind-narrowed read
  // is what kept the online-quota REGISTRY venues out of the «Онлайн» table.
  const full = useDashboard(undefined);
  const scoped = useDashboard(region, department, undefined, format);

  // ADDITIVE (T.Z42 → T.Z56) — the option counts come from the #6 × #25.5 join
  // (`useDepartmentJoin`), scoped to the selected region: the RESPONSE rows are
  // department-filtered now and would make every option count the survivors only.
  const venueDirectory = useVenueDirectory();
  const regionDirectory = useMemo(() => {
    if (!region) return undefined; // «Все регионы» — the hook reads the whole roster
    if (venueDirectory.data === undefined) return null; // still loading → bare labels
    return venueDirectory.data.filter((venue) => venue.region === region);
  }, [region, venueDirectory.data]);
  const { counts: departmentCounts } = useDepartmentJoin(regionDirectory ?? undefined);
  const departmentOptions = useMemo(() => departmentFilterOptions(departmentCounts), [departmentCounts]);

  const data: DashboardDto | undefined = scoped.data?.data;

  const isLoading = scoped.isPending;
  const isError = scoped.isError;
  const error = scoped.error;
  const refetch = scoped.refetch;

  // T.V5 — the control spins while the request its own change triggered is in
  // flight. `isFetching` rather than `isLoading` is the flag on purpose: a filter
  // change is almost always a background reload of a cached key.
  const regionFetching = scoped.isFetching;

  const regions = useMemo(() => regionsOf(full.data?.data.venues ?? []), [full.data]);
  // The rows arrive department-filtered (server-side); T.Z145 — UN-narrowed by kind,
  // so the fold below is the only membership decision. The model cuts each table by
  // its quota axis AND sorts it on that axis (the axes disagree about a row: a branch
  // is 2 600 online and 0 offline), so the pre-cut page-level sort is gone.
  // ADDITIVE (T.Z149) — the partner branches leave the regular online cut into their
  // own group tables, and the block the group bar reads travels next to the rows.
  const tables = useMemo(
    () => venueTables(data?.venues ?? [], kind, sort, dir, data?.partnerPools),
    [data, kind, sort, dir],
  );
  // T.Z144 — the counter stays a VENUE count: a venue with both quotas is listed on
  // both axes (once per table), so the caption counts distinct ids across the visible
  // tables — never promising a venue no table carries, never double-counting one the
  // two axes share. T.Z149 — the partner group tables count like any other table.
  const visibleCount = useMemo(() => {
    const ids = new Set<number>();
    for (const row of tables.offline?.rows ?? []) ids.add(row.venueId);
    for (const row of tables.online?.rows ?? []) ids.add(row.venueId);
    for (const group of tables.partners) for (const row of group.rows) ids.add(row.venueId);
    return ids.size;
  }, [tables]);

  const setParam = (key: string, value: ParamValue) => setParams({ [key]: value });
  const toggleSort = (key: SortKey) =>
    setParams({
      [PARAM_SORT]: key,
      // Clicking the active column flips it; a new column starts where a
      // manager means it: numbers descending, names ascending.
      [PARAM_DIR]: sort === key ? (dir === 'asc' ? 'desc' : 'asc') : undefined,
    });

  // T.Z144 — the drill-down carries the axis the clicked TABLE reads, not the page
  // filter's: the table's «Заявления» cell counts its own format since v1.9.45 (T.Z156),
  // so the landing list must answer exactly those rows — an unfiltered list would show
  // more children than the cell printed (the owner's МФЮА case, read the other way).
  // Under a selected kind the table axis IS the filter axis (`kindFormat(kind)`), so the
  // URLs stay as T.Z144/T.Z145 shipped them; «вместе» now carries the axis too (the
  // T.Z145 byte-for-byte note ends with v1.9.45 — see `drillDownSearch`).
  const openVenue = (row: DashboardRowDto, axis?: VenueAxis) =>
    navigate(`${APPLICATIONS_PATH}${drillDownSearch(row.venueId, region, axis)}`);

  const noRows = data !== undefined && visibleCount === 0;
  const anyFilter = Boolean(region) || department !== undefined || kind !== undefined;

  return (
    <>
      <div className="page-header">
        <h1>Дашборд</h1>
        <p className="page-header__sub">Заполненность компании по площадкам.</p>
      </div>

      {/* ADDITIVE (T.Z171, owner 22.09) — the folded status explainer, the FIRST block
          of the page (above the filter strip and the cards grid): closed it is one
          summary line, unfolded it explains the status rules the whole screen reads.
          Static content over the shared status vocabulary — no api call, no contract
          surface; the wording lives in `./StatusExplainer` next to its sources. */}
      <StatusExplainer />

      <div className="dashboard-toolbar">
        <div className="dashboard-toolbar__field dashboard-toolbar__field--region">
          <label className="field__label" htmlFor="dashboard-region">
            Регион
          </label>
          <div className="dashboard-toolbar__control">
            <Select
              id="dashboard-region"
              value={region ?? ''}
              onChange={(event) => setParam(PARAM_REGION, event.target.value)}
            >
              <option value="">Все регионы</option>
              {regions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
            {regionFetching ? <span className="dashboard-toolbar__busy" aria-hidden="true" /> : null}
          </div>
        </div>
        {/* ADDITIVE (T.Z42; server-side since T.Z56/v1.8.9) — the department filter.
            A venue reaches its bucket only through its managers. The select writes the
            URL key, the api narrows the whole response — cards AND rows. */}
        <div className="dashboard-toolbar__field">
          <label className="field__label" htmlFor="dashboard-department">
            Отдел
          </label>
          <Select
            id="dashboard-department"
            data-testid="dashboard-department"
            value={department ?? ''}
            onChange={(event) => setParam(PARAM_DEPARTMENT, event.target.value)}
          >
            {departmentOptions.map((option) => (
              <option key={option.value} value={option.value} title={option.title}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
        {/* ADDITIVE (T.Y1, v1.9.35) — the «Тип площадки» filter. The values are the
            roster's «Тип» tokens (REGISTRY / PARTNER_ONLINE, A5/T.Z18); the labels are
            the owner's three states («вместе» is the default and sends nothing).
            T.Z144 — a selected kind names the quota AXIS: the tables retitle («Офлайн»
            / «Онлайн») and every register drill-in carries
            `format=OFFLINE|ONLINE`. CORRECTED (T.Z145, owner: «quota based approach») —
            the kind never narrows the READ again: every venue with a quota on the axis
            shows, of either kind, and the venues without one leave the tables. */}
        <div className="dashboard-toolbar__field">
          <label className="field__label" htmlFor="dashboard-kind">
            Тип площадки
          </label>
          <Select
            id="dashboard-kind"
            data-testid="dashboard-kind"
            value={kind ?? ''}
            onChange={(event) => setParam(PARAM_KIND, event.target.value)}
          >
            {kindFilterOptions(venueDirectory.data).map((option) => (
              <option key={option.value} value={option.value} title={option.title}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
        {data ? (
          <p className="dashboard-toolbar__meta" data-testid="dashboard-total">
            {/* T.Z56 — `meta.total` is the DEPARTMENT-scoped row count now (the api
                narrows the response), so the counter needs no client-side branch.
                T.Z144/T.Z145 — the quota cut applies in EVERY filter state now (it is
                the only membership decision), so the caption counts the VISIBLE rows
                across the tables — distinct ids, since a both-quotas venue lists twice. */}
            {formatCount(visibleCount)} площадок
            {region ? ` · ${region}` : ''}
            {department !== undefined ? ` · ${DEPARTMENT_BUCKET_LABEL[department]}` : ''}
            {kind !== undefined ? ` · ${kindFilterLabel(kind)}` : ''}
          </p>
        ) : null}
      </div>

      {isLoading ? (
        <DashboardSkeleton />
      ) : isError ? (
        <div className="card">
          <ErrorState
            message={error instanceof Error ? error.message : 'Не удалось загрузить компанию.'}
            onRetry={() => void refetch()}
          />
        </div>
      ) : !data ? (
        <div className="card">
          <EmptyState title="Нет данных" hint="Компания пока не сформирована." />
        </div>
      ) : (
        <>
          <CampaignCards campaign={data.campaign} format={format} />

          {/* ADDITIVE (v1.9.23, T.Z105) — the contract funnel of the T.Z101 sync, the
              block the owner asked for right after the «Прошёл ЕВИ» card (it follows
              the summary grid, so it is the first block after the status buckets).
              The counts are the campaign's own, already scoped by the filters above —
              T.Z144: including the axis `format=`, which the section links carry. */}
          <ContractFunnel funnel={data.campaign.contractFunnel} format={format} />

          {/* T.Z144 — the tables the filter names. «вместе»: the offline table first,
              the «Онлайн-площадки» below it. T.Z145 — every table pages with its own
              pager state; the axis decides the cells, the filter decides the drill. */}
          {tables.offline ? (
            <VenueTable
              sectionTestId="dashboard-section-offline"
              tableTestId="dashboard-table"
              plan={tables.offline}
              sort={sort}
              dir={dir}
              onSort={toggleSort}
              onOpen={openVenue}
            />
          ) : null}
          {tables.online ? (
            <VenueTable
              sectionTestId="dashboard-section-online"
              tableTestId="dashboard-table-online"
              plan={tables.online}
              sort={sort}
              dir={dir}
              onSort={toggleSort}
              onOpen={openVenue}
            />
          ) : null}

          {/* ADDITIVE (T.Z149, owner 20.09) — the partner GROUP tables: «Академия Топ
              (общая квота)» and any further partner, one per partner, AFTER the regular
              online table. A partner's filials hold no quotas of their own, so the
              table's «Заполненность» is ONE shared progress bar over the partner's pool
              (in the section head), not a per-venue column. */}
          {tables.partners.map((group) => (
            <PartnerVenueTable
              key={group.partnerId}
              group={group}
              sort={sort}
              dir={dir}
              onSort={toggleSort}
              onOpen={openVenue}
            />
          ))}

          {noRows ? (
            <div className="card card--flush">
              <EmptyState
                title={anyFilter ? 'В регионе нет площадок' : 'Нет площадок'}
                hint={anyFilter ? 'Фильтр ничего не отобрал — ослабьте условие.' : 'Импортируйте квоты, чтобы увидеть заполненность.'}
                action={
                  anyFilter ? (
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() =>
                        setParams({ [PARAM_REGION]: undefined, [PARAM_DEPARTMENT]: undefined, [PARAM_KIND]: undefined })
                      }
                    >
                      Сбросить фильтр
                    </button>
                  ) : undefined
                }
              />
            </div>
          ) : null}
          <p className="dashboard-footnote">
            Нажмите на площадку, чтобы открыть её заявления
            {region ? ' с сохранёнными фильтрами' : ''}.
          </p>
        </>
      )}
    </>
  );
}

/** The pager's «Показаны X–Y из N» caption — the honest count a page shows. */
function rangeCaption(rows: number, page: number, pageSize: number): string {
  const pages = totalPagesOf(rows, pageSize);
  const safePage = clampPage(page, pages);
  const from = rows === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, rows);
  return `Показаны ${from}–${to} из ${rows}`;
}

/**
 * One venue table: its heading (T.Z144 — the title FOLLOWS the filter: «Офлайн»,
 * «Онлайн», «Офлайн-площадки», «Онлайн-площадки»), the seven columns, and its own
 * client-side pager (T.Z144 gave it to the online table; T.Z145 — to every table,
 * the offline ones included, so no axis can hand back a 230-row wall). The axis
 * decides which numbers the «План»/«Заполненность» — and since v1.9.45 (T.Z156) the
 * «Заявления» — cells read; the drill-in carries that same axis `format=`, so the
 * landing list counts the rows the cells printed.
 */
function VenueTable({
  plan,
  sort,
  dir,
  onSort,
  onOpen,
  sectionTestId,
  tableTestId,
}: {
  /** Title + the full quota-cut sorted rows + the axis (see {@link VenueTablePlan}). */
  plan: VenueTablePlan;
  sort: SortKey;
  dir: 'asc' | 'desc';
  onSort: (key: SortKey) => void;
  onOpen: (row: DashboardRowDto, axis?: VenueAxis) => void;
  sectionTestId: string;
  tableTestId: string;
}) {
  // The pager is the table's own state, NOT a URL filter: the page's filters answer
  // «which venues», the pager only answers «which screenful» — and a shareable link
  // lands on page 1, which is the reading order anyway. It resets when the row set
  // changes identity (a filter, a sort, a refetch that grew the table), so a pager
  // never points past the last row; between 60s refetches of the same data the page
  // survives (the count is what moves it, not the poll). T.Z145 — a sort change lands
  // on page 1 of the FULL sorted cut: `plan.rows` is sorted whole by the model
  // (`venueTables`), the slice below only picks the screenful.
  const [page, setPage] = useState(1);
  const rowCount = plan.rows.length;
  useEffect(() => {
    setPage(1);
  }, [rowCount, sort, dir, plan.title]);
  if (rowCount === 0) return null;

  const pages = totalPagesOf(rowCount, VENUE_TABLE_PAGE_SIZE);
  const safePage = clampPage(page, pages);
  const rows = pageSlice(plan.rows, safePage, VENUE_TABLE_PAGE_SIZE);
  const bodyId = `${sectionTestId}-body`;
  return (
    <section className="card card--flush" data-testid={sectionTestId} aria-label={plan.title}>
      <header className="dashboard-section__head">
        <h2 className="dashboard-section__title" data-testid={`${sectionTestId}-title`}>
          {plan.title}
        </h2>
      </header>
      <div className="dashboard-section__body" id={bodyId}>
        <TableWrap>
          <table className="data" data-testid={tableTestId}>
            <thead>
              <tr>
                {/* The column plan is the model's (`sectionColumns`) — one list, so the
                    two tables cannot drift: each reads the same seven columns on its
                    own axis. */}
                {sectionColumns().map((column) =>
                  column.sortKey ? (
                    <Th
                      key={column.key}
                      label={column.label}
                      sortKey={column.sortKey}
                      sort={sort}
                      dir={dir}
                      onSort={onSort}
                      numeric={column.numeric}
                    />
                  ) : (
                    <th key={column.key} className={column.numeric ? 'num' : undefined}>
                      {column.label}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <VenueRow key={row.venueId} row={row} onOpen={onOpen} axis={plan.axis} />
              ))}
            </tbody>
          </table>
        </TableWrap>
        <Pagination
          meta={{ page: safePage, totalPages: pages, total: rowCount, limit: VENUE_TABLE_PAGE_SIZE }}
          onPage={setPage}
          rangeCaption={rangeCaption(rowCount, safePage, VENUE_TABLE_PAGE_SIZE)}
        />
      </div>
    </section>
  );
}

/**
 * ADDITIVE (T.Z149, owner 20.09, v1.9.41) — one partner's GROUP table: «Академия Топ
 * (общая квота)». The columns are the online table's MINUS the per-venue
 * «Заполненность» — a partner's filials hold no quotas of their own, so the group's
 * fill is ONE progress bar over the SHARED pool, rendered in the section head with its
 * «заполнено N из M» caption (`data-testid="partner-pool-bar"` /
 * `partner-pool-caption`, the section `dashboard-section-partner`, the table
 * `dashboard-table-partner`). The rows keep the online table's idioms: the axis cells
 * read the shared pool, the pager slices AFTER the model's full-cut sort, a sort or a
 * row-set change lands on page 1 — the exact T.Z145 idioms, one more table of them.
 */
function PartnerVenueTable({
  group,
  sort,
  dir,
  onSort,
  onOpen,
}: {
  group: PartnerTablePlan;
  sort: SortKey;
  dir: 'asc' | 'desc';
  onSort: (key: SortKey) => void;
  onOpen: (row: DashboardRowDto, axis?: VenueAxis) => void;
}) {
  const [page, setPage] = useState(1);
  const rowCount = group.rows.length;
  useEffect(() => {
    setPage(1);
  }, [rowCount, sort, dir, group.title]);
  if (rowCount === 0) return null;

  const pages = totalPagesOf(rowCount, VENUE_TABLE_PAGE_SIZE);
  const safePage = clampPage(page, pages);
  const rows = pageSlice(group.rows, safePage, VENUE_TABLE_PAGE_SIZE);
  const caption = `Заполнено ${formatCount(group.filled)} из ${formatCount(group.plan)}`;
  // The shared pool is the «План» of every row on this table's axis — the same number
  // the head bar reads — and the fill column is GONE (one bar per group, never per row).
  const columns = sectionColumns().filter((column) => column.key !== 'fillPercent');
  return (
    <section className="card card--flush" data-testid="dashboard-section-partner" data-partner-id={group.partnerId} aria-label={group.title}>
      <header className="dashboard-section__head dashboard-section__head--pool">
        <h2 className="dashboard-section__title" data-testid="dashboard-section-partner-title">
          {group.title}
        </h2>
        <div className="dashboard-pool" data-testid="partner-pool">
          <FillBar
            percent={group.fillPercent}
            label={`Общая квота ${group.partnerName}: ${caption} (${group.fillPercent}%)`}
          />
          <span className="dashboard-pool__caption" data-testid="partner-pool-caption" title={`Общая квота партнёра — одна на все ${rowCount} филиалов`}>
            {caption}
          </span>
        </div>
      </header>
      <div className="dashboard-section__body">
        <TableWrap>
          <table className="data" data-testid="dashboard-table-partner">
            <thead>
              <tr>
                {columns.map((column) =>
                  column.sortKey ? (
                    <Th
                      key={column.key}
                      label={column.label}
                      sortKey={column.sortKey}
                      sort={sort}
                      dir={dir}
                      onSort={onSort}
                      numeric={column.numeric}
                    />
                  ) : (
                    <th key={column.key} className={column.numeric ? 'num' : undefined}>
                      {column.label}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <VenueRow key={row.venueId} row={row} onOpen={onOpen} axis="ONLINE" hideFill />
              ))}
            </tbody>
          </table>
        </TableWrap>
        <Pagination
          meta={{ page: safePage, totalPages: pages, total: rowCount, limit: VENUE_TABLE_PAGE_SIZE }}
          onPage={setPage}
          rangeCaption={rangeCaption(rowCount, safePage, VENUE_TABLE_PAGE_SIZE)}
        />
      </div>
    </section>
  );
}

function VenueRow({
  row,
  onOpen,
  axis,
  hideFill,
}: {
  row: DashboardRowDto;
  onOpen: (row: DashboardRowDto, axis?: VenueAxis) => void;
  axis?: VenueAxis;
  /**
   * ADDITIVE (T.Z149) — the partner group table's marker: the fill cell is omitted
   * (the group's ONE bar lives in the section head), the columns stay aligned.
   */
  hideFill?: boolean;
}) {
  // T.Z144 — the cells read the table's axis: the «Онлайн» tables print the ONLINE
  // plan (the branch's shared pool included — the pool IS that axis' plan) and the
  // ONLINE fill; the offline tables read the row's own `planOffline` pair (T.Z145 —
  // the own-type read borrowed the pool onto a branch's offline row).
  const partner = isPartnerRow(row);
  const plan = axis === 'ONLINE' ? onlinePlanOf(row) : offlinePlanOf(row);
  const fill = axis === 'ONLINE' ? onlineFillOf(row) : offlineFillOf(row);
  // ADDITIVE (T.Z156, v1.9.45) — the «Заявления» cell counts the TABLE'S OWN format:
  // the whole-funnel map mixed offline children into the online table (the owner's
  // МФЮА row) and online children into the offline one. An all-zero axis map renders
  // the absence marker — the honest «this venue has no applications of this format» —
  // and the drill below lands on exactly the rows the cell counted.
  const byStatus = axisByStatusOf(row, axis);
  // T.Z144 — `data-axis` is always NAMED: the «вместе» offline table reads the
  // own-type (offline) pair, so its rows declare OFFLINE too — a selector never
  // has to guess which table a row came from. T.Z145 — the drill's `format` used to
  // be the FILTER's (`kindFormat(kind)`); T.Z156 moved it to the TABLE's axis so the
  // landing list matches the cell counts (see `drillDownSearch`).
  const open = () => onOpen(row, axis);
  return (
    <tr
      data-testid="dashboard-row"
      data-venue-id={row.venueId}
      data-kind={row.kind ?? 'REGISTRY'}
      data-axis={axis ?? 'OFFLINE'}
      tabIndex={0}
      className="dashboard-row"
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
    >
      <td className="cell-primary">
        <button
          type="button"
          className="link-btn"
          data-testid="venue-link"
          onClick={(event) => {
            // The button is the accessible path, the row the shortcut: stop one of
            // the two navigations.
            event.stopPropagation();
            open();
          }}
        >
          {row.name}
        </button>
      </td>
      <td data-testid="row-kind">
        <Badge
          tone={partner ? 'warn' : 'neutral'}
          title={partner ? 'Филиал партнёра — план задаёт общий пул партнёра' : 'Реестровая площадка офлайн-набора'}
        >
          {kindBadgeLabel(row.kind)}
        </Badge>
      </td>
      <td className={row.region ? undefined : 'cell-muted'} data-testid="row-region">
        {row.region ?? '—'}
      </td>
      <td className="num" data-testid="row-plan">
        {formatCount(plan)}
      </td>
      <td>
        <StatusBadges byStatus={byStatus} compact />
      </td>
      {/* T.Z149 — the partner group's row carries NO fill cell: its table's ONE bar sits
          in the section head, so the column list and the cells stay in step. */}
      {hideFill ? null : <td><FillBar percent={fill} label={`Заполненность ${row.name}: ${fill}%`} /></td>}
      <td className="cell-muted" data-testid="control-status" title={CONTROL_HINT}>
        {CONTROL_PLACEHOLDER}
      </td>
    </tr>
  );
}

/** Sortable column header: a button with `aria-sort`, the active one marked. */
function Th({
  label,
  sortKey,
  sort,
  dir,
  onSort,
  numeric,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortKey;
  dir: 'asc' | 'desc';
  onSort: (key: SortKey) => void;
  numeric?: boolean;
}) {
  const active = sort === sortKey;
  return (
    <th
      className={numeric ? 'num' : undefined}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <button
        type="button"
        className={active ? 'sort-btn sort-btn--active' : 'sort-btn'}
        data-testid={`sort-${sortKey}`}
        onClick={() => onSort(sortKey)}
      >
        {label}
        <span className="sort-btn__arrow" aria-hidden="true">
          {active ? (dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  );
}

/**
 * Skeleton of the exact surface it stands in for — cards first, then the table
 * with the same columns, so nothing jumps when the data lands.
 */
function DashboardSkeleton() {
  const columns = ['Площадка', 'Тип', 'Регион', 'План', 'Заявления', 'Заполненность', 'Контроль'];

  return (
    <div data-testid="dashboard-skeleton">
      <CampaignCardsSkeleton />
      {/* T.Z81 — the one flush card that keeps `overflow: clip`: its table is the
          card's FIRST child, so the header strip paints flush against the rounded
          top corners (nothing else in the app does), and it hosts no combobox the
          clip could shear. See theme.css `.card--flush--clip`. */}
      <div className="card card--flush card--flush--clip">
        <TableWrap>
          <table className="data">
            <thead>
              <tr>
                {columns.map((label) => (
                  <th key={label}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }, (_, index) => (
                <tr key={index}>
                  <td>
                    <div className="skeleton skeleton--line skeleton--wide" />
                  </td>
                  <td>
                    <div className="skeleton skeleton--line" />
                  </td>
                  <td>
                    <div className="skeleton skeleton--line" />
                  </td>
                  <td>
                    <div className="skeleton skeleton--line skeleton--num" />
                  </td>
                  <td>
                    <div className="skeleton skeleton--line skeleton--num" />
                  </td>
                  <td>
                    <div className="skeleton skeleton--line skeleton--wide" />
                  </td>
                  <td>
                    <div className="skeleton skeleton--line" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </div>
    </div>
  );
}
